/*
 * URLs of the share images (Open Graph / Twitter cards) for each kind of page.
 * Safe to import from pages and components: it only builds strings. The images
 * themselves are drawn by src/pages/api/og/[...path].js.
 *
 * Every URL is absolute (social crawlers do not resolve relative ones) and
 * names what the page is about, never the words on the image.
 */

import config from '@/config';
import i18n from '@/i18n.json';
import routes from '@/share-image-routes';

const {
  DEFAULT_SHARE_IMAGE_PATH,
  SHARE_IMAGE_EXTENSION,
  SHARE_IMAGE_HEIGHT,
  SHARE_IMAGE_PATH,
  SHARE_IMAGE_TYPE,
  SHARE_IMAGE_VERSION,
  SHARE_IMAGE_WIDTH,
} = routes;

/** What `og:image:width`, `og:image:height` and `og:image:type` say: the same for every image. */
export const SHARE_IMAGE = {
  width: SHARE_IMAGE_WIDTH,
  height: SHARE_IMAGE_HEIGHT,
  type: SHARE_IMAGE_TYPE,
};

/**
 * The committed image (public/images/social/og-default.jpg): the home card in
 * English. Used by every page without a card of its own, and by the image
 * function when it cannot draw one.
 */
export const defaultShareImage = () => `${config.rootUrl}${DEFAULT_SHARE_IMAGE_PATH}`;

const card = (locale, ...segments) => {
  // A missing slug would address another card, or none: use the default image.
  if (segments.some((segment) => typeof segment !== 'string' || segment === '')) {
    return defaultShareImage();
  }

  const path = segments.map((segment) => encodeURIComponent(segment)).join('/');

  return `${config.rootUrl}${SHARE_IMAGE_PATH}/${SHARE_IMAGE_VERSION}/${
    i18n.locales.includes(locale) ? locale : i18n.defaultLocale
  }/${path}${SHARE_IMAGE_EXTENSION}`;
};

export const shareImage = {
  /** `/`. English is the committed file, so the most shared URL never needs the function. */
  home: (locale) =>
    !locale || locale === i18n.defaultLocale ? defaultShareImage() : card(locale, 'home'),
  /** `/buy` */
  browse: (locale) => card(locale, 'browse'),
  /** `/countries` */
  countries: (locale) => card(locale, 'countries'),
  /** `/brands/{country}` and `/buy/{country}` */
  country: (countrySlug, locale) => card(locale, 'country', countrySlug),
  /** `/buy/{country}/{category}` for a gift card category */
  countryCategory: (countrySlug, categorySlug, locale) =>
    card(locale, 'country', countrySlug, categorySlug),
  /** `/shop/{category}` */
  category: (categorySlug, locale) => card(locale, 'category', categorySlug),
  /** `/brands/{country}/{brand}` */
  brand: (countrySlug, brandSlug, locale) => card(locale, 'brand', countrySlug, brandSlug),
  /** `/currency` */
  currencyIndex: (locale) => card(locale, 'currency'),
  /** `/currency/{code}`: the lowercase code, as in the page URL */
  currency: (code, locale) => card(locale, 'currency', code && String(code).toLowerCase()),
  /** `/networks` */
  networks: (locale) => card(locale, 'networks'),
  /** `/networks/{network}` */
  network: (networkSlug, locale) => card(locale, 'networks', networkSlug),
  /** `/agents` */
  agents: (locale) => card(locale, 'agents'),
  /** `/contact` */
  contact: (locale) => card(locale, 'contact'),
};
